/**
 * La mesa del laboratorio: qué muestras hay que procesar y dónde se cargan
 * los resultados.
 *
 * Abre por lo pendiente, no por el catálogo: quien entra aquí en su turno
 * viene a trabajar la cola, y las urgentes van primero. El catálogo está en
 * otra pestaña porque se toca una vez al mes.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Search, TriangleAlert } from "lucide-react";
import {
  type Examen,
  type OrdenItem,
  type OrdenLab,
  actualizarExamen,
  crearExamen,
  entregarOrden,
  listExamenes,
  listOrdenes,
  registrarResultado,
  tomarMuestra,
} from "../api/laboratorio";
import { formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { useZonaHoraria } from "../auth/TenantBrandingProvider";
import { fechaHora } from "../lib/fecha";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Dialog,
  Field,
  Input,
} from "../components/ui";

const soles = (v: string | number | null | undefined) =>
  `S/ ${Number(v ?? 0).toFixed(2)}`;

export function LaboratorioPage() {
  const { me } = useAuth();
  const tz = useZonaHoraria();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"cola" | "catalogo">("cola");
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState<{ orden: OrdenLab; item: OrdenItem } | null>(
    null,
  );

  const puedeProcesar = me?.permisos.includes("laboratorio:procesar") ?? false;

  const { data: ordenes = [], isLoading } = useQuery({
    queryKey: ["lab", "cola"],
    queryFn: () => listOrdenes({ por_procesar: true }),
    refetchInterval: 60_000,
  });
  const { data: listas = [] } = useQuery({
    queryKey: ["lab", "listas"],
    queryFn: () => listOrdenes({ estado: "CON_RESULTADOS" }),
  });

  // Las dos acciones se confirman. Ninguna borra nada, pero las dos afirman
  // algo sobre el mundo físico —"la muestra ya está tomada", "el paciente ya
  // tiene su resultado en la mano"— y decirlo por error deja a alguien
  // esperando un tubo que nadie sacó o un papel que nadie entregó.
  const [porTomar, setPorTomar] = useState<OrdenLab | null>(null);
  const [porEntregar, setPorEntregar] = useState<OrdenLab | null>(null);

  const muestra = useMutation({
    mutationFn: (id: number) => tomarMuestra(id),
    onSuccess: () => {
      setError(null);
      setPorTomar(null);
      queryClient.invalidateQueries({ queryKey: ["lab"] });
    },
    onError: (e) => {
      setPorTomar(null);
      setError(formatApiError(e, "No se pudo registrar la muestra."));
    },
  });
  const entregar = useMutation({
    mutationFn: (id: number) => entregarOrden(id),
    onSuccess: () => {
      setPorEntregar(null);
      queryClient.invalidateQueries({ queryKey: ["lab"] });
    },
    onError: (e) => {
      setPorEntregar(null);
      setError(formatApiError(e, "No se pudo marcar como entregada."));
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <FlaskConical className="h-6 w-6 text-[color:var(--color-link)]" />
            Laboratorio
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Muestras por procesar y carga de resultados.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-sm">
          {[
            { id: "cola" as const, label: `Cola (${ordenes.length})` },
            { id: "catalogo" as const, label: "Catálogo" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-md px-3 py-1.5 font-medium transition ${
                tab === t.id
                  ? "bg-[color:var(--color-link)] text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <Alert>{error}</Alert>}

      {tab === "catalogo" ? (
        <CatalogoExamenes onError={setError} />
      ) : (
        <>
          <Card>
            <CardHeader>
              <h2 className="font-semibold text-slate-900">Por procesar</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Pagadas y esperando resultados. Las urgentes van primero.
              </p>
            </CardHeader>
            <CardBody className="p-0">
              {isLoading && (
                <p className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</p>
              )}
              {!isLoading && ordenes.length === 0 && (
                <p className="px-6 py-10 text-center text-sm text-slate-500">
                  Nada pendiente. Las órdenes aparecen aquí en cuanto caja las cobra.
                </p>
              )}
              <ul className="divide-y divide-slate-100">
                {ordenes.map((o) => (
                  <li key={o.id} className="px-6 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
                          {o.paciente_nombre}
                          {o.urgente && <Badge variant="danger">Urgente</Badge>}
                          <Badge variant={o.estado === "EN_PROCESO" ? "warning" : "neutral"}>
                            {o.estado === "EN_PROCESO" ? "Muestra tomada" : "Sin muestra"}
                          </Badge>
                        </p>
                        <p className="text-xs text-slate-500">
                          Orden #{o.id} · {fechaHora(o.fecha, tz)} ·{" "}
                          {o.profesional_nombre ?? "—"}
                          {o.diagnostico_presuntivo
                            ? ` · ${o.diagnostico_presuntivo}`
                            : ""}
                        </p>
                      </div>
                      {puedeProcesar && o.estado === "SOLICITADA" && (
                        <Button onClick={() => setPorTomar(o)}>
                          Registrar toma de muestra
                        </Button>
                      )}
                    </div>

                    <ul className="mt-2 divide-y divide-slate-50">
                      {o.items.map((i) => (
                        <li
                          key={i.id}
                          className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-sm"
                        >
                          <span className="text-slate-800">{i.descripcion}</span>
                          {i.tipo_muestra && (
                            <span className="text-xs text-slate-400">
                              {i.tipo_muestra}
                            </span>
                          )}
                          {i.preparacion && (
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">
                              {i.preparacion}
                            </span>
                          )}
                          {i.tiene_resultado ? (
                            <span
                              className={`font-mono ${
                                i.fuera_de_rango
                                  ? "font-semibold text-red-600"
                                  : "text-slate-900"
                              }`}
                            >
                              {i.valor ?? "informe"} {i.unidad ?? ""}
                            </span>
                          ) : null}
                          {puedeProcesar && (
                            <button
                              type="button"
                              onClick={() => setCargando({ orden: o, item: i })}
                              className="ml-auto text-xs font-medium text-[color:var(--color-link)] hover:underline"
                            >
                              {i.tiene_resultado ? "Corregir" : "Cargar resultado"} →
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          {listas.length > 0 && (
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Listas para entregar</h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  Con todos sus resultados. El médico ya las ve en su bandeja.
                </p>
              </CardHeader>
              <CardBody className="p-0">
                <ul className="divide-y divide-slate-100">
                  {listas.map((o) => (
                    <li
                      key={o.id}
                      className="flex flex-wrap items-center gap-3 px-6 py-3 text-sm"
                    >
                      <span className="font-medium text-slate-900">
                        {o.paciente_nombre}
                      </span>
                      <span className="text-xs text-slate-500">
                        Orden #{o.id} · {o.items.length} exámenes
                      </span>
                      {o.items.some((i) => i.fuera_de_rango) && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                          <TriangleAlert className="h-3 w-3" />
                          valores fuera de rango
                        </span>
                      )}
                      {puedeProcesar && (
                        <Button
                          variant="ghost"
                          className="ml-auto"
                          onClick={() => setPorEntregar(o)}
                        >
                          Marcar entregada
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
        </>
      )}

      {cargando && (
        <ResultadoDialog
          orden={cargando.orden}
          item={cargando.item}
          onClose={() => setCargando(null)}
          onSaved={() => {
            setCargando(null);
            setError(null);
            queryClient.invalidateQueries({ queryKey: ["lab"] });
            queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        open={porTomar !== null}
        onClose={() => setPorTomar(null)}
        onConfirm={() => porTomar && muestra.mutate(porTomar.id)}
        loading={muestra.isPending}
        variant="primary"
        title="Registrar toma de muestra"
        description="Confirma que la muestra del paciente ya está tomada. La orden pasa a 'en proceso' y el médico ve que su examen está en marcha."
        detalle={
          porTomar && (
            <>
              <span className="font-medium">{porTomar.paciente_nombre}</span>
              <br />
              {porTomar.items.map((i) => i.descripcion).join(", ")}
            </>
          )
        }
        confirmLabel="Sí, muestra tomada"
        cancelLabel="Todavía no"
      />

      <ConfirmDialog
        open={porEntregar !== null}
        onClose={() => setPorEntregar(null)}
        onConfirm={() => porEntregar && entregar.mutate(porEntregar.id)}
        loading={entregar.isPending}
        variant="primary"
        title="Marcar como entregada"
        description="Confirma que el paciente ya tiene sus resultados. La orden se cierra y sale de la cola."
        detalle={
          porEntregar && (
            <>
              <span className="font-medium">{porEntregar.paciente_nombre}</span>
              <br />
              Orden #{porEntregar.id} · {porEntregar.items.length} exámenes
              {porEntregar.items.some((i) => i.fuera_de_rango) && (
                <span className="mt-1 block text-red-600">
                  Tiene valores fuera de rango.
                </span>
              )}
            </>
          )
        }
        confirmLabel="Sí, ya la entregué"
        cancelLabel="Todavía no"
      />
    </div>
  );
}

function ResultadoDialog({
  orden,
  item,
  onClose,
  onSaved,
  onError,
}: {
  orden: OrdenLab;
  item: OrdenItem;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [valor, setValor] = useState(item.valor ?? "");
  const [texto, setTexto] = useState(item.texto ?? "");
  const [unidad, setUnidad] = useState(item.unidad ?? "");
  const [referencia, setReferencia] = useState(item.valor_referencia ?? "");
  const [fuera, setFuera] = useState(item.fuera_de_rango);

  const guardar = useMutation({
    mutationFn: () =>
      registrarResultado(orden.id, item.id, {
        valor: valor.trim() || null,
        texto: texto.trim() || null,
        unidad: unidad.trim() || null,
        valor_referencia: referencia.trim() || null,
        fuera_de_rango: fuera,
      }),
    onSuccess: onSaved,
    onError: (e) => onError(formatApiError(e, "No se pudo guardar el resultado.")),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={item.descripcion}
      description={`${orden.paciente_nombre} · orden #${orden.id}`}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Valor" htmlFor="r_valor">
            <Input
              id="r_valor"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              className="font-mono"
              placeholder="13.2"
            />
          </Field>
          <Field label="Unidad" htmlFor="r_uni">
            <Input
              id="r_uni"
              value={unidad}
              onChange={(e) => setUnidad(e.target.value)}
              placeholder="g/dL"
            />
          </Field>
          <Field label="Valor de referencia" htmlFor="r_ref">
            <Input
              id="r_ref"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="12 - 16"
            />
          </Field>
        </div>

        <Field
          label="Informe"
          htmlFor="r_texto"
          hint="Para lo que no da un número: una ecografía, un cultivo, una descripción."
        >
          <textarea
            id="r_texto"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />
        </Field>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={fuera}
            onChange={(e) => setFuera(e.target.checked)}
          />
          Fuera del rango de referencia
          <span className="text-xs text-slate-500">
            — se resalta en rojo para el médico
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => guardar.mutate()}
            disabled={(!valor.trim() && !texto.trim()) || guardar.isPending}
          >
            Guardar resultado
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function CatalogoExamenes({ onError }: { onError: (m: string) => void }) {
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [q, setQ] = useState("");
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const puedeEditar = me?.permisos.includes("laboratorio:catalogo") ?? false;

  const { data: examenes = [] } = useQuery({
    queryKey: ["lab", "examenes", q],
    queryFn: () => listExamenes({ q: q.trim() || undefined }),
  });

  const toggle = useMutation({
    mutationFn: (e: Examen) => actualizarExamen(e.id, { activo: !e.activo }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lab", "examenes"] }),
    onError: (e) => onError(formatApiError(e, "No se pudo actualizar.")),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex grow items-center gap-3">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar examen por nombre, código o área…"
              className="w-full border-0 p-0 text-sm focus:outline-none"
            />
          </div>
          {puedeEditar && (
            <Button onClick={() => setNuevoOpen(true)}>Nuevo examen</Button>
          )}
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-6 py-2 font-medium">Examen</th>
                <th className="px-3 py-2 font-medium">Área</th>
                <th className="px-3 py-2 font-medium">Muestra</th>
                <th className="px-3 py-2 font-medium">Preparación</th>
                <th className="px-3 py-2 text-right font-medium">Resultado</th>
                <th className="px-3 py-2 text-right font-medium">Precio</th>
                <th className="px-6 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {examenes.map((e) => (
                <tr key={e.id} className={e.activo ? "" : "opacity-50"}>
                  <td className="px-6 py-2">
                    <span className="text-slate-900">{e.nombre}</span>
                    <span className="ml-2 font-mono text-xs text-slate-400">
                      {e.codigo}
                    </span>
                    {e.requiere_cita && (
                      <Badge variant="warning">Requiere cita</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{e.area ?? "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{e.tipo_muestra ?? "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {e.preparacion ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">
                    {e.horas_resultado != null ? `${e.horas_resultado} h` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-slate-900">
                    {soles(e.precio)}
                  </td>
                  <td className="px-6 py-2 text-right">
                    {puedeEditar && (
                      <button
                        type="button"
                        onClick={() => toggle.mutate(e)}
                        className="text-xs font-medium text-[color:var(--color-link)] hover:underline"
                      >
                        {e.activo ? "Desactivar" : "Activar"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>

      {nuevoOpen && (
        <NuevoExamenDialog
          onClose={() => setNuevoOpen(false)}
          onSaved={() => {
            setNuevoOpen(false);
            queryClient.invalidateQueries({ queryKey: ["lab", "examenes"] });
          }}
          onError={onError}
        />
      )}
    </Card>
  );
}

function NuevoExamenDialog({
  onClose,
  onSaved,
  onError,
}: {
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [form, setForm] = useState<Partial<Examen>>({
    codigo: "",
    nombre: "",
    area: "",
    tipo_muestra: "",
    preparacion: "",
    horas_resultado: 24,
    unidad: "",
    valor_referencia: "",
    requiere_cita: false,
    precio: 0,
  });

  const guardar = useMutation({
    mutationFn: () =>
      crearExamen({
        ...form,
        precio: Number(form.precio) || 0,
        horas_resultado: Number(form.horas_resultado) || null,
      }),
    onSuccess: onSaved,
    onError: (e) => onError(formatApiError(e, "No se pudo crear el examen.")),
  });

  const set = (k: keyof Examen, v: unknown) => setForm({ ...form, [k]: v });

  return (
    <Dialog open onClose={onClose} title="Nuevo examen" size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Código" htmlFor="e_cod">
            <Input
              id="e_cod"
              value={form.codigo ?? ""}
              onChange={(e) => set("codigo", e.target.value.toUpperCase())}
              className="font-mono"
              placeholder="LAB-013"
            />
          </Field>
          <Field label="Nombre" htmlFor="e_nom">
            <Input
              id="e_nom"
              value={form.nombre ?? ""}
              onChange={(e) => set("nombre", e.target.value)}
            />
          </Field>
          <Field label="Área" htmlFor="e_area">
            <Input
              id="e_area"
              value={form.area ?? ""}
              onChange={(e) => set("area", e.target.value)}
              placeholder="Bioquímica"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Tipo de muestra" htmlFor="e_mues">
            <Input
              id="e_mues"
              value={form.tipo_muestra ?? ""}
              onChange={(e) => set("tipo_muestra", e.target.value)}
              placeholder="Sangre"
            />
          </Field>
          <Field
            label="Preparación"
            htmlFor="e_prep"
            hint="Se le dice al paciente antes de que se vaya."
          >
            <Input
              id="e_prep"
              value={form.preparacion ?? ""}
              onChange={(e) => set("preparacion", e.target.value)}
              placeholder="Ayuno de 8 horas"
            />
          </Field>
          <Field label="Horas hasta el resultado" htmlFor="e_horas">
            <Input
              id="e_horas"
              type="number"
              value={String(form.horas_resultado ?? "")}
              onChange={(e) => set("horas_resultado", e.target.value)}
              className="font-mono"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Unidad" htmlFor="e_uni">
            <Input
              id="e_uni"
              value={form.unidad ?? ""}
              onChange={(e) => set("unidad", e.target.value)}
              placeholder="mg/dL"
            />
          </Field>
          <Field label="Valor de referencia" htmlFor="e_ref">
            <Input
              id="e_ref"
              value={form.valor_referencia ?? ""}
              onChange={(e) => set("valor_referencia", e.target.value)}
              placeholder="70 - 100"
            />
          </Field>
          <Field label="Precio (S/)" htmlFor="e_precio">
            <Input
              id="e_precio"
              type="number"
              step="0.10"
              value={String(form.precio ?? 0)}
              onChange={(e) => set("precio", e.target.value)}
              className="font-mono"
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={!!form.requiere_cita}
            onChange={(e) => set("requiere_cita", e.target.checked)}
          />
          Requiere cita
          <span className="text-xs text-slate-500">
            — ocupa equipo y horario, admisión le da hora en vez de atenderlo por
            orden de llegada
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => guardar.mutate()}
            disabled={!form.codigo || !form.nombre || guardar.isPending}
          >
            Crear examen
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
